import {
  PropsWithChildren,
  ReactNode,
  isValidElement,
  cloneElement,
  ReactElement,
} from 'react'

type HighlightTextWrapperProps = {
  searchText?: string
  highlightClassName?: string
  caseSensitive?: boolean
  dataTestId?: string
} & PropsWithChildren

const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

const highlightText = (
  text: string,
  searchText: string,
  highlightClassName: string,
  caseSensitive: boolean,
): ReactNode[] => {
  if (!searchText.trim()) {
    return [text]
  }

  const flags = caseSensitive ? 'g' : 'gi'
  const regex = new RegExp(`(${escapeRegExp(searchText)})`, flags)
  const parts = text.split(regex)

  return parts.map((part, index) => {
    const isMatch = regex.test(part)
    regex.lastIndex = 0

    if (isMatch) {
      return (
        <span key={index} className={highlightClassName}>
          {part}
        </span>
      )
    }
    return part
  })
}

const processReactNode = (
  node: ReactNode,
  searchText: string,
  highlightClassName: string,
  caseSensitive: boolean,
): ReactNode => {
  if (typeof node === 'string') {
    return highlightText(node, searchText, highlightClassName, caseSensitive)
  }

  if (typeof node === 'number') {
    return highlightText(
      String(node),
      searchText,
      highlightClassName,
      caseSensitive,
    )
  }

  if (isValidElement(node)) {
    const element = node as ReactElement

    const processedChildren = processChildren(
      (element.props as { children?: ReactNode }).children,
      searchText,
      highlightClassName,
      caseSensitive,
    )

    return cloneElement(element, {}, processedChildren)
  }

  if (Array.isArray(node)) {
    return node.map((child) =>
      processReactNode(
        child as ReactNode,
        searchText,
        highlightClassName,
        caseSensitive,
      ),
    )
  }

  return node
}

const processChildren = (
  children: ReactNode,
  searchText: string,
  highlightClassName: string,
  caseSensitive: boolean,
): ReactNode => {
  if (!children) {
    return children
  }

  if (Array.isArray(children)) {
    return children.map((child) =>
      processReactNode(
        child as ReactNode,
        searchText,
        highlightClassName,
        caseSensitive,
      ),
    )
  }

  return processReactNode(
    children,
    searchText,
    highlightClassName,
    caseSensitive,
  )
}

export const HighlightTextWrapper = ({
  children,
  searchText = '',
  highlightClassName = 'font-bold',
  caseSensitive = false,
  dataTestId,
}: HighlightTextWrapperProps) => {
  if (!searchText.trim()) {
    return <>{children}</>
  }

  const processedChildren = processChildren(
    children,
    searchText,
    highlightClassName,
    caseSensitive,
  )

  return <div data-testid={dataTestId}>{processedChildren}</div>
}
